import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { Html5Qrcode } from 'html5-qrcode';
import { api } from '../utils/api';
import { toast } from 'react-toastify';

interface ProductDetail {
  id: number;
  product_id: number;
  product_type_id: number | null;
  width: number | null;
  height: number | null;
  number: number;
  status: string | null;
  barcode: string;
  type_of_work: string | null;
  created_at: string;
  updated_at: string;
}

interface Product {
  id: number;
  product_name: string;
  room: string | null;
  status: string | null;
  note: string | null;
  product_type_id: number | null;
  order: number | null;
  job_id: number;
  job_number: string | null;
  job_address: string | null;
  apartment: string | null;
  customer_name: string | null;
  created_at: string;
  updated_at: string;
}

interface BarcodeSearchResult {
  product_detail: ProductDetail;
  product: Product;
}

interface ScannedItem extends BarcodeSearchResult {
  scannedAt: string;
  selected: boolean;
}

interface TypeOfWorkOption {
  id: number;
  workName: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MaterialStatus {
  id: number;
  statusName: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function BarcodeScan() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [scanning, setScanning] = useState(false);
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraId, setCameraId] = useState<string | null>(null);
  const [productData, setProductData] = useState<BarcodeSearchResult | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [scanMethod, setScanMethod] = useState<'camera' | 'file'>('camera');
  const [processingFile, setProcessingFile] = useState(false);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isScannerRunningRef = useRef<boolean>(false);
  const scannerId = 'barcode-scanner';
  
  // Multiple scan mode
  const [multipleScanMode, setMultipleScanMode] = useState(false);
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [savingBulkStatus, setSavingBulkStatus] = useState(false);
  
  // New state for editing
  const [materialStatuses, setMaterialStatuses] = useState<MaterialStatus[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingDimensions, setSavingDimensions] = useState(false);
  const [savingTypeOfWork, setSavingTypeOfWork] = useState(false);
  const [typeOfWorkOptions, setTypeOfWorkOptions] = useState<TypeOfWorkOption[]>([]);
  const [statusForm, setStatusForm] = useState({
    status: '',
  });
  const [dimensionForm, setDimensionForm] = useState({
    width: '',
    height: '',
  });
  const [typeOfWorkForm, setTypeOfWorkForm] = useState<string[]>([]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount - handle scanner cleanup defensively
      const scanner = html5QrCodeRef.current;
      if (!scanner) {
        return;
      }
      
      // If scanner is not running, just clear it
      if (!isScannerRunningRef.current) {
        try {
          scanner.clear();
        } catch (clearErr) {
          // Ignore clear errors
        }
        html5QrCodeRef.current = null;
        return;
      }
      
      // Try to stop the scanner if it's running
      // Wrap in try-catch to handle any synchronous errors
      try {
        scanner.stop()
          .then(() => {
            try {
              scanner.clear();
            } catch (clearErr) {
              // Ignore clear errors
            }
            html5QrCodeRef.current = null;
            isScannerRunningRef.current = false;
          })
          .catch((err: any) => {
            // Handle promise rejection - silently ignore "not running" errors
            const errorMsg = err?.message || String(err) || '';
            // Don't log "not running" or "not paused" errors
            if (!errorMsg.includes('not running') && !errorMsg.includes('not paused')) {
              console.debug('[Camera Scan] Cleanup stop error (ignored):', errorMsg);
            }
            // Always clear even if stop failed
            try {
              scanner.clear();
            } catch (clearErr) {
              // Ignore clear errors
            }
            html5QrCodeRef.current = null;
            isScannerRunningRef.current = false;
          });
      } catch (syncErr: any) {
        // Handle any synchronous errors
        const errorMsg = syncErr?.message || String(syncErr) || '';
        if (!errorMsg.includes('not running') && !errorMsg.includes('not paused')) {
          console.debug('[Camera Scan] Cleanup sync error (ignored):', errorMsg);
        }
        try {
          scanner.clear();
        } catch (clearErr) {
          // Ignore clear errors
        }
        html5QrCodeRef.current = null;
        isScannerRunningRef.current = false;
      }
    };
  }, []);

  // Load material statuses
  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        // Load material statuses
        const statusResponse = await api.get<MaterialStatus[]>('/product-detail-status');
        setMaterialStatuses(statusResponse.data.filter(s => s.isActive));

        // Load type of work options
        const typeOfWorkResponse = await api.get<TypeOfWorkOption[]>('/type-of-work');
        setTypeOfWorkOptions(typeOfWorkResponse.data.filter(t => t.isActive));
      } catch (err: any) {
        console.error('Error loading options:', err);
        // Don't show error to user, just log it
      } finally {
        setLoadingOptions(false);
      }
    };
    
    loadOptions();
  }, []);

  // Update forms when product data changes
  useEffect(() => {
    if (productData?.product_detail) {
      const detail = productData.product_detail;
      setStatusForm({
        status: detail.status || '',
      });
      setDimensionForm({
        width: detail.width ? String(detail.width) : '',
        height: detail.height ? String(detail.height) : '',
      });
      setTypeOfWorkForm(
        detail.type_of_work ? detail.type_of_work.split(',').map(s => s.trim()).filter(Boolean) : []
      );
    }
  }, [productData]);

  const startScanning = async () => {
    console.log('[Camera Scan] Starting camera scan...');
    try {
      setError(null);
      setScannedCode(null);

      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        console.error('[Camera Scan] Camera API not available');
        setError(
          'Camera API is not available. This may be due to: ' +
          '1) Browser does not support camera access, ' +
          '2) Browser permissions are blocked, ' +
          '3) Camera hardware is not available. ' +
          'Please check your browser settings and ensure a camera is connected.'
        );
        return;
      }

      // Allow HTTP for localhost and private IP ranges
      // Browsers will handle security restrictions, but we won't block attempts
      // Note: Browsers may still require HTTPS for non-localhost, but we'll attempt anyway
      // The browser will show its own error if camera access is blocked

      // Stop any existing scanner instance first
      if (html5QrCodeRef.current) {
        try {
          await html5QrCodeRef.current.stop();
          html5QrCodeRef.current.clear();
          isScannerRunningRef.current = false;
        } catch (err: any) {
          // Ignore errors when stopping existing instance, especially "not running or paused"
          const errorMsg = err?.message || '';
          if (!errorMsg.includes('not running') && !errorMsg.includes('not paused')) {
            console.debug('[Camera Scan] Error stopping existing instance (ignored):', errorMsg);
          }
          try {
            html5QrCodeRef.current?.clear();
          } catch (clearErr) {
            // Ignore clear errors
          }
          isScannerRunningRef.current = false;
        }
        html5QrCodeRef.current = null;
      }

      // Ensure the scanner element exists in the DOM
      const scannerElement = document.getElementById(scannerId);
      if (!scannerElement) {
        console.error('[Camera Scan] Scanner element not found in DOM:', scannerId);
        setError('Scanner element not found. Please refresh the page and try again.');
        return;
      }

      console.log('[Camera Scan] Scanner element found, initializing Html5Qrcode...');
      const html5QrCode = new Html5Qrcode(scannerId);
      html5QrCodeRef.current = html5QrCode;

      // Get available cameras
      let devices: { id: string; label: string }[] = [];
      try {
        console.log('[Camera Scan] Requesting camera access...');
        devices = await Html5Qrcode.getCameras();
        console.log('[Camera Scan] Available cameras:', devices);
      } catch (cameraErr: any) {
        const cameraErrorMsg = cameraErr?.message || 'Unknown error';
        console.warn('[Camera Scan] getCameras() failed:', cameraErrorMsg, '— will try facingMode fallback');

        // If it's a permission denied error, don't try fallback
        if (cameraErrorMsg.includes('Permission') || cameraErrorMsg.includes('permission') ||
            cameraErrorMsg.includes('NotAllowedError') || cameraErrorMsg.includes('Not allowed')) {
          setError('Camera permission denied. Please allow camera access in your browser settings and try again.');
          html5QrCodeRef.current = null;
          isScannerRunningRef.current = false;
          return;
        }
        // For other errors (NotFound, etc.), fall through to facingMode fallback below
      }

      // Determine camera source: use device ID if available, otherwise facingMode fallback
      let cameraSource: string | { facingMode: string };

      if (devices && devices.length > 0) {
        // Prefer rear camera (back camera) over front camera for better barcode scanning
        let selectedCameraId = cameraId;

        if (!selectedCameraId) {
          try {
            // Strategy 1: Try to get rear camera using facingMode constraint (most reliable on mobile)
            try {
              const testStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' }
              });
              const track = testStream.getVideoTracks()[0];
              const settings = track.getSettings();
              const rearCameraId = settings.deviceId;

              const matchedDevice = devices.find(d => d.id === rearCameraId);
              if (matchedDevice) {
                selectedCameraId = rearCameraId as string | null;
                console.log('[Camera Scan] Selected rear camera using facingMode constraint');
              }

              track.stop();
              testStream.getTracks().forEach(t => t.stop());
            } catch (constraintErr) {
              console.debug('[Camera Scan] facingMode constraint not available, trying alternative methods');
            }

            // Strategy 2: Check camera labels for rear camera indicators
            if (!selectedCameraId) {
              try {
                const mediaDevices = await navigator.mediaDevices.enumerateDevices();
                const videoInputs = mediaDevices.filter(device => device.kind === 'videoinput');

                for (const device of devices) {
                  const mediaDevice = videoInputs.find(v => v.deviceId === device.id);
                  if (mediaDevice?.label) {
                    const lowerLabel = mediaDevice.label.toLowerCase();
                    if (lowerLabel.includes('back') ||
                        lowerLabel.includes('rear') ||
                        lowerLabel.includes('environment') ||
                        (lowerLabel.includes('camera') && !lowerLabel.includes('front') && !lowerLabel.includes('user'))) {
                      selectedCameraId = device.id;
                      console.log('[Camera Scan] Selected rear camera based on label:', mediaDevice.label);
                      break;
                    }
                  }
                }
              } catch (enumErr) {
                console.debug('[Camera Scan] Could not enumerate devices for label check:', enumErr);
              }
            }

            // Strategy 3: Heuristic fallback
            if (!selectedCameraId) {
              if (devices.length > 1) {
                selectedCameraId = devices[devices.length - 1].id;
                console.log('[Camera Scan] Selected last camera (heuristic - likely rear camera on mobile)');
              } else {
                selectedCameraId = devices[0].id;
                console.log('[Camera Scan] Selected only available camera');
              }
            }
          } catch (err) {
            console.warn('[Camera Scan] Error selecting camera, using first available:', err);
            selectedCameraId = devices[0].id;
          }
        }

        setCameraId(selectedCameraId);
        console.log('[Camera Scan] Selected camera:', selectedCameraId);
        cameraSource = selectedCameraId!;
      } else {
        // No devices from getCameras() — use facingMode as fallback
        console.log('[Camera Scan] No device list available, using facingMode: environment fallback');
        cameraSource = { facingMode: 'environment' };
      }

      try {
        console.log('[Camera Scan] Starting camera stream...');
        await html5QrCode.start(
          cameraSource,
            {
              fps: 10,
              qrbox: function(viewfinderWidth, viewfinderHeight) {
                // For barcodes (CODE128), use a wider scanning area since barcodes are rectangular
                // Use 80% of width and 50% of height for better barcode detection
                // Ensure minimum size of 50px as required by html5-qrcode
                const minSize = 50;
                const calculatedWidth = Math.floor(viewfinderWidth * 0.8);
                const calculatedHeight = Math.floor(viewfinderHeight * 0.5);
                const finalWidth = Math.max(calculatedWidth, minSize);
                const finalHeight = Math.max(calculatedHeight, minSize);
                
                console.log('[Camera Scan] QR box dimensions:', {
                  viewfinder: { width: viewfinderWidth, height: viewfinderHeight },
                  calculated: { width: calculatedWidth, height: calculatedHeight },
                  final: { width: finalWidth, height: finalHeight }
                });
                
                return {
                  width: finalWidth,
                  height: finalHeight
                };
              },
              aspectRatio: 1.0,
              // Note: html5-qrcode supports barcodes by default, though optimized for QR codes
              // It will attempt to decode various barcode formats including CODE_128, EAN, UPC, etc.
              // For better barcode detection, we use a wider scanning box
            },
            (decodedText) => {
              // Success callback - barcode scanned
              console.log('[Camera Scan] Barcode detected (raw):', decodedText);
              
              // Clean the scanned barcode - remove any whitespace, newlines, or control characters
              let cleanedBarcode = decodedText.trim().replace(/[\r\n\t]/g, '').replace(/\s+/g, '');
              
              // Remove any non-printable characters
              cleanedBarcode = cleanedBarcode.replace(/[\x00-\x1F\x7F]/g, '');
              
              console.log('[Camera Scan] Barcode cleaned:', cleanedBarcode);
              
              console.log('[Camera Scan] Final barcode:', cleanedBarcode);
              setScannedCode(cleanedBarcode);
              
              // In multiple scan mode, add to list and continue scanning
              // In single scan mode, stop scanning and show single item
              if (multipleScanMode) {
                // Add to list and continue scanning
                lookupProductByBarcodeForList(cleanedBarcode);
                // Don't stop scanning - continue for next item
              } else {
                // Single scan mode - stop scanning
                setScanning(false);
                // Always try to stop, but ignore "not running or paused" errors
                html5QrCode.stop()
                  .then(() => {
                    isScannerRunningRef.current = false;
                  })
                  .catch((err: any) => {
                    // Ignore stop errors, especially "not running or paused"
                    const errorMsg = err?.message || '';
                    if (!errorMsg.includes('not running') && !errorMsg.includes('not paused')) {
                      console.debug('[Camera Scan] Error stopping after scan (ignored):', errorMsg);
                    }
                    isScannerRunningRef.current = false;
                  });
                // Look up product by barcode
                lookupProductByBarcode(cleanedBarcode);
              }
            },
            (errorMessage) => {
              // Error callback - ignore scanning errors, just keep scanning
              // Only show errors if they're not related to scanning
              if (!errorMessage.includes('No MultiFormat Readers') && 
                  !errorMessage.includes('NotFoundException') &&
                  !errorMessage.includes('No QR code')) {
                // This is expected during scanning, so we ignore it
                console.debug('[Camera Scan] Scanning error (ignored):', errorMessage);
              }
            }
          );

          console.log('[Camera Scan] Camera stream started successfully');
          setScanning(true);
          isScannerRunningRef.current = true;
        } catch (startErr: any) {
          const startErrorMsg = startErr?.message || 'Unknown error';
          console.error('[Camera Scan] Failed to start camera stream:', startErrorMsg, startErr);
          let errorMessage = '';
          
          if (startErrorMsg.includes('Permission') || startErrorMsg.includes('permission')) {
            errorMessage = 'Camera permission denied. Please allow camera access in your browser settings and try again.';
          } else if (startErrorMsg.includes('NotAllowedError')) {
            errorMessage = 'Camera access was denied. Please check your browser permissions. If using HTTP on a non-localhost connection, you may need to use HTTPS.';
          } else if (startErrorMsg.includes('NotReadableError')) {
            errorMessage = 'Camera is already in use by another application. Please close other applications using the camera and try again.';
          } else if (startErrorMsg.includes('NotFoundError') || startErrorMsg.includes('not found')) {
            errorMessage = 'No camera found. Please ensure your device has a camera connected.';
          } else if (startErrorMsg.includes('SecurityError') || startErrorMsg.includes('security')) {
            errorMessage = 'Security error: Camera access blocked. This usually means the page is not in a secure context. ' +
              'For non-localhost connections, browsers typically require HTTPS. Try using HTTPS or access from localhost.';
          } else {
            errorMessage = `Failed to start camera: ${startErrorMsg}. ` +
              'If using HTTP on a non-localhost connection, try using HTTPS or access from localhost.';
          }
          
          setError(errorMessage);
          setScanning(false);
          html5QrCodeRef.current = null;
          isScannerRunningRef.current = false;
        }
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to start camera';
      console.error('[Camera Scan] Unexpected error during camera initialization:', errorMessage, err);
      let userFriendlyMessage = errorMessage;
      
      // Provide more helpful error messages
      if (errorMessage.includes('Permission') || errorMessage.includes('permission')) {
        userFriendlyMessage = 'Camera permission denied. Please allow camera access in your browser settings and try again.';
      } else if (errorMessage.includes('NotAllowedError')) {
        userFriendlyMessage = 'Camera access was denied. Please check your browser permissions. If using HTTP on a non-localhost connection, you may need to use HTTPS.';
      } else if (errorMessage.includes('NotFoundError') || errorMessage.includes('not found')) {
        userFriendlyMessage = 'No camera found. Please ensure your device has a camera connected.';
      } else if (errorMessage.includes('NotReadableError')) {
        userFriendlyMessage = 'Camera is already in use by another application. Please close other applications using the camera.';
      } else if (errorMessage.includes('OverconstrainedError')) {
        userFriendlyMessage = 'Camera does not support the required settings. Please try a different camera.';
      } else if (errorMessage.includes('SecurityError') || errorMessage.includes('security')) {
        userFriendlyMessage = 'Security error: Camera access blocked. This usually means the page is not in a secure context. ' +
          'For non-localhost connections, browsers typically require HTTPS. Try using HTTPS or access from localhost.';
      } else {
        userFriendlyMessage = errorMessage + 
          ' If using HTTP on a non-localhost connection, try using HTTPS or access from localhost.';
      }
      
      setError(userFriendlyMessage);
      setScanning(false);
      
      // Clean up on error - always try to stop if we have a reference
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current
          .stop()
          .then(() => {
            html5QrCodeRef.current?.clear();
            html5QrCodeRef.current = null;
            isScannerRunningRef.current = false;
          })
          .catch((err: any) => {
            // Ignore cleanup errors, especially "not running or paused"
            const errorMsg = err?.message || '';
            if (!errorMsg.includes('not running') && !errorMsg.includes('not paused')) {
              console.debug('[Camera Scan] Error cleanup (ignored):', errorMsg);
            }
            try {
              html5QrCodeRef.current?.clear();
            } catch (clearErr) {
              // Ignore clear errors
            }
            html5QrCodeRef.current = null;
            isScannerRunningRef.current = false;
          });
      }
    }
  };

  const stopScanning = async () => {
    console.log('[Camera Scan] Stopping camera scan...');
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
        isScannerRunningRef.current = false;
        setScanning(false);
        setError(null);
        console.log('[Camera Scan] Camera scan stopped successfully');
      } catch (err: any) {
        // Don't log or show error if scanner is already stopped
        const errorMsg = err?.message || '';
        if (!errorMsg.includes('not running') && !errorMsg.includes('not paused')) {
          console.error('[Camera Scan] Error stopping camera:', errorMsg, err);
          setError(errorMsg || 'Failed to stop camera');
        } else {
          console.debug('[Camera Scan] Scanner already stopped (ignored)');
        }
        // Still reset state even if stop fails
        try {
          html5QrCodeRef.current?.clear();
        } catch (clearErr) {
          // Ignore clear errors
        }
        html5QrCodeRef.current = null;
        isScannerRunningRef.current = false;
        setScanning(false);
      }
    } else {
      console.warn('[Camera Scan] No active scanner instance to stop');
      // Reset state even if scanner wasn't running
      html5QrCodeRef.current = null;
      isScannerRunningRef.current = false;
      setScanning(false);
    }
  };

  const lookupProductByBarcode = async (barcode: string) => {
    console.log('[Camera Scan] Looking up product for barcode:', barcode);
    setLoadingProduct(true);
    setProductData(null);
    try {
      const url = `/products/barcode/${encodeURIComponent(barcode)}`;
      const response = await api.get<BarcodeSearchResult>(url);
      console.log('[Camera Scan] Product found:', response.data);
      setProductData(response.data);
    } catch (err: any) {
      if (err.response?.status === 404) {
        console.warn('[Camera Scan] Product not found for barcode:', barcode);
        setError('Product not found for this barcode. The barcode may not be registered in the system.');
      } else {
        console.error('[Camera Scan] Failed to look up product:', err.response?.data?.message || err.message || 'Unknown error', err);
        setError(`Failed to look up product: ${err.response?.data?.message || err.message || 'Unknown error'}`);
      }
    } finally {
      setLoadingProduct(false);
    }
  };

  const lookupProductByBarcodeForList = async (barcode: string, suppressToast: boolean = false) => {
    console.log('[Camera Scan] Looking up product for list, barcode:', barcode);
    try {
      // Check if this barcode is already in the list before making API call
      const existingIndex = scannedItems.findIndex(
        item => item.product_detail.barcode === barcode
      );
      
      if (existingIndex >= 0) {
        // Barcode already exists in the list, don't add it again
        console.log('[Camera Scan] Barcode already exists in list, skipping:', barcode);
        setError(`Barcode "${barcode}" is already in the scanned items list. Duplicate entries are not allowed.`);
        return;
      }
      
      const url = `/products/barcode/${encodeURIComponent(barcode)}`;
      const response = await api.get<BarcodeSearchResult>(url);
      console.log('[Camera Scan] Product found for list:', response.data);
      
      // Double-check before adding (in case state changed during API call)
      setScannedItems(prev => {
        const duplicateIndex = prev.findIndex(
          item => item.product_detail.barcode === barcode
        );
        
        if (duplicateIndex >= 0) {
          // Barcode was added while API call was in progress, don't add duplicate
          console.log('[Camera Scan] Barcode was added during API call, skipping duplicate:', barcode);
          setError(`Barcode "${barcode}" is already in the scanned items list. Duplicate entries are not allowed.`);
          return prev;
        }
        
        // Add new item
        const newItems = [
          ...prev,
          {
            ...response.data,
            scannedAt: new Date().toISOString(),
            selected: true, // Auto-select new items
          },
        ];
        
        // Show toast notification with scan count (unless suppressed)
        if (!suppressToast) {
          const scanCount = newItems.length;
          toast.success(`Barcode scanned! Total items: ${scanCount}`, {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }
        
        return newItems;
      });
    } catch (err: any) {
      if (err.response?.status === 404) {
        console.warn('[Camera Scan] Product not found for barcode:', barcode);
        setError(`Product not found for barcode: ${barcode}. The barcode may not be registered in the system.`);
      } else {
        console.error('[Camera Scan] Failed to look up product:', err.response?.data?.message || err.message || 'Unknown error', err);
        setError(`Failed to look up product: ${err.response?.data?.message || err.message || 'Unknown error'}`);
      }
    }
  };

  const clearScannedCode = () => {
    setScannedCode(null);
    setProductData(null);
    setStatusForm({
      status: '',
    });
    setDimensionForm({
      width: '',
      height: '',
    });
    setTypeOfWorkForm([]);
  };

  // Multiple scan mode functions
  const toggleItemSelection = (index: number) => {
    setScannedItems(prev => {
      const updated = [...prev];
      updated[index].selected = !updated[index].selected;
      return updated;
    });
  };

  const toggleSelectAll = () => {
    const allSelected = scannedItems.every(item => item.selected);
    setScannedItems(prev => prev.map(item => ({ ...item, selected: !allSelected })));
  };

  const removeItem = (index: number) => {
    setScannedItems(prev => prev.filter((_, i) => i !== index));
  };

  const clearScannedItems = () => {
    setScannedItems([]);
    setBulkStatus('');
  };

  const handleBulkStatusUpdate = async () => {
    const selectedItems = scannedItems.filter(item => item.selected);
    if (selectedItems.length === 0) {
      setError('Please select at least one item to update');
      return;
    }

    if (!bulkStatus) {
      setError('Please select a material status');
      return;
    }

    setSavingBulkStatus(true);
    setError(null);

    try {
      // Update all selected items in parallel
      const updatePromises = selectedItems.map(item =>
        api.put(
          `/products/${item.product.id}/details/${item.product_detail.id}`,
          { status: bulkStatus }
        )
      );

      await Promise.all(updatePromises);

      // Refresh the items in the list
      const refreshPromises = selectedItems.map(item =>
        api.get<BarcodeSearchResult>(`/products/barcode/${encodeURIComponent(item.product_detail.barcode)}`)
      );

      const refreshedItems = await Promise.all(refreshPromises);

      // Update the list with refreshed data
      setScannedItems(prev => {
        const updated = [...prev];
        refreshedItems.forEach((response, idx) => {
          const originalItem = selectedItems[idx];
          const itemIndex = updated.findIndex(
            item => item.product_detail.barcode === originalItem.product_detail.barcode
          );
          if (itemIndex >= 0) {
            updated[itemIndex] = {
              ...response.data,
              scannedAt: updated[itemIndex].scannedAt,
              selected: updated[itemIndex].selected,
            };
          }
        });
        return updated;
      });

      setBulkStatus('');
      setError(null);
    } catch (err: any) {
      console.error('Error updating bulk status:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update statuses');
    } finally {
      setSavingBulkStatus(false);
    }
  };

  // Function #1: Change Material Status
  const handleStatusChange = async () => {
    if (!productData?.product_detail) return;
    
    setSavingStatus(true);
    setError(null);
    
    try {
      const { product_detail, product } = productData;
      
      // Only update if status changed
      if (statusForm.status !== product_detail.status) {
        await api.put(
          `/products/${product.id}/details/${product_detail.id}`,
          { status: statusForm.status || null }
        );
        
        // Refresh product data
        await lookupProductByBarcode(scannedCode || '');
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update material status');
    } finally {
      setSavingStatus(false);
    }
  };

  // Function #2: Add/Edit Glass Sizes (Dimensions)
  const handleDimensionUpdate = async () => {
    if (!productData?.product_detail) return;
    
    setSavingDimensions(true);
    setError(null);
    
    try {
      const { product_detail, product } = productData;
      
      const updateData: any = {};
      const currentWidth = product_detail.width ? String(product_detail.width) : '';
      const currentHeight = product_detail.height ? String(product_detail.height) : '';
      
      if (dimensionForm.width !== currentWidth) {
        updateData.width = dimensionForm.width ? parseFloat(dimensionForm.width) : null;
      }
      
      if (dimensionForm.height !== currentHeight) {
        updateData.height = dimensionForm.height ? parseFloat(dimensionForm.height) : null;
      }
      
      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        await api.put(
          `/products/${product.id}/details/${product_detail.id}`,
          updateData
        );
        
        // Refresh product data
        await lookupProductByBarcode(scannedCode || '');
      }
    } catch (err: any) {
      console.error('Error updating dimensions:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update dimensions');
    } finally {
      setSavingDimensions(false);
    }
  };

  // Function #3: Set Type of Work
  const handleTypeOfWorkUpdate = async () => {
    if (!productData?.product_detail) return;

    setSavingTypeOfWork(true);
    setError(null);

    try {
      const { product_detail, product } = productData;

      const newValue = typeOfWorkForm.length > 0 ? typeOfWorkForm.join(', ') : null;
      if (newValue !== (product_detail.type_of_work || null)) {
        await api.put(
          `/products/${product.id}/details/${product_detail.id}`,
          { type_of_work: newValue }
        );

        // Refresh product data
        await lookupProductByBarcode(scannedCode || '');
      }
    } catch (err: any) {
      console.error('Error updating type of work:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update type of work');
    } finally {
      setSavingTypeOfWork(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setProcessingFile(true);
    setError(null);
    setScannedCode(null);

    try {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
        setProcessingFile(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setError('File is too large. Please upload an image smaller than 10MB.');
        setProcessingFile(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Use backend API for more reliable barcode scanning
      const formData = new FormData();
      formData.append('image', file);
      
      let decodedText: string | null = null;
      let decodedBarcodes: string[] = [];
      
      try {
        // Don't set Content-Type header - let axios set it automatically with boundary for FormData
        const response = await api.post<{ barcode: string; barcodes?: string[] }>('/products/barcode/scan', formData);
        
        // Check if multiple barcodes were detected
        if (response.data.barcodes && response.data.barcodes.length > 1) {
          decodedBarcodes = response.data.barcodes;
          decodedText = response.data.barcode; // Keep for backward compatibility
        } else {
          decodedText = response.data.barcode;
          decodedBarcodes = response.data.barcode ? [response.data.barcode] : [];
        }
      } catch (apiErr: any) {
        // If backend fails, fall back to frontend scanning
        // Create a temporary hidden element for the scanner (html5-qrcode requires a DOM element)
        const tempId = 'temp-file-scanner';
        let tempElement = document.getElementById(tempId);
        if (!tempElement) {
          tempElement = document.createElement('div');
          tempElement.id = tempId;
          tempElement.style.display = 'none';
          tempElement.style.position = 'absolute';
          tempElement.style.visibility = 'hidden';
          document.body.appendChild(tempElement);
        }
        
        const html5QrCode = new Html5Qrcode(tempId);
        
        try {
          decodedText = await html5QrCode.scanFile(file, false);
        } finally {
          try {
            html5QrCode.clear();
          } catch (clearErr) {
            // Ignore clear errors
          }
          if (tempElement && tempElement.parentNode) {
            tempElement.parentNode.removeChild(tempElement);
          }
        }
        
        // If frontend also fails, throw the original backend error
        if (!decodedText && apiErr.response?.status !== 404) {
          throw apiErr;
        }
      }
      
      // Process barcodes - handle multiple barcodes if detected
      if (decodedBarcodes.length > 0) {
        // If multiple barcodes detected, automatically enable multiple scan mode
        if (decodedBarcodes.length > 1 && !multipleScanMode) {
          setMultipleScanMode(true);
          setScannedCode(null);
          setProductData(null);
        }
        
        // Process each barcode
        const validBarcodes: string[] = [];
        
        for (const barcode of decodedBarcodes) {
          // Clean the scanned barcode - remove any whitespace, newlines, or control characters
          let cleanedBarcode = barcode.trim().replace(/[\r\n\t]/g, '').replace(/\s+/g, '');
          
          // Remove any non-printable characters
          cleanedBarcode = cleanedBarcode.replace(/[\x00-\x1F\x7F]/g, '');
          
          validBarcodes.push(cleanedBarcode);
        }

        // Process valid barcodes
        if (validBarcodes.length > 0) {
          if (validBarcodes.length === 1) {
            // Single barcode - use existing logic
            const cleanedBarcode = validBarcodes[0];
            setScannedCode(cleanedBarcode);
            if (multipleScanMode) {
              lookupProductByBarcodeForList(cleanedBarcode);
            } else {
              lookupProductByBarcode(cleanedBarcode);
            }
          } else {
            // Multiple barcodes - add all to list
            setScannedCode(null);
            setProductData(null);
            
            // Process all barcodes sequentially (suppress individual toasts)
            for (const cleanedBarcode of validBarcodes) {
              await lookupProductByBarcodeForList(cleanedBarcode, true);
            }
            
            // Show success message
            toast.success(`${validBarcodes.length} barcodes detected and added to list!`, {
              position: 'top-right',
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
          }
        } else {
          setError('No barcodes found in the image. Please try again.');
        }
      } else if (decodedText && decodedText.trim()) {
        // Fallback to single barcode processing (for backward compatibility)
        // Clean the scanned barcode - remove any whitespace, newlines, or control characters
        let cleanedBarcode = decodedText.trim().replace(/[\r\n\t]/g, '').replace(/\s+/g, '');
        
        // Remove any non-printable characters
        cleanedBarcode = cleanedBarcode.replace(/[\x00-\x1F\x7F]/g, '');
        
        setScannedCode(cleanedBarcode);
        if (multipleScanMode) {
          lookupProductByBarcodeForList(cleanedBarcode);
        } else {
          lookupProductByBarcode(cleanedBarcode);
        }
      } else {
        setError('Could not read barcode from image. Please ensure the barcode is clear, well-lit, and not blurry. Try taking a new photo.');
      }
    } catch (err: any) {
      
      const errorMsg = err?.message || 'Failed to scan barcode from image';
      
      // Provide more helpful error messages
      if (errorMsg.includes('No QR code') || 
          errorMsg.includes('NotFoundException') || 
          errorMsg.includes('No MultiFormat') ||
          errorMsg.includes('not found') ||
          errorMsg.includes('No barcode')) {
        setError('No barcode found in the image. Please ensure:\n' +
                 '• The barcode is clearly visible and in focus\n' +
                 '• The image is well-lit\n' +
                 '• The barcode is not damaged or obscured\n' +
                 '• The barcode is a CODE128 format (as used in this system)\n\n' +
                 'If the issue persists, try taking a new photo with better lighting or focus.');
      } else if (errorMsg.includes('format') || errorMsg.includes('Format')) {
        setError('Unsupported image format. Please use JPG, PNG, GIF, or WebP format.');
      } else {
        setError(`Failed to scan image: ${errorMsg}\n\n` +
                 'Tips for better scanning:\n' +
                 '• Ensure the barcode is clearly visible\n' +
                 '• Use good lighting\n' +
                 '• Avoid blurry or low-resolution images\n' +
                 '• Try taking a new photo with better lighting or focus');
      }
    } finally {
      setProcessingFile(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };


  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Tier Items
        </Typography>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Barcode Scanner
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Scan barcodes using camera or upload an image.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={multipleScanMode}
                  onChange={(e) => {
                    setMultipleScanMode(e.target.checked);
                    if (e.target.checked) {
                      // Clear single item view when switching to multiple mode
                      setScannedCode(null);
                      setProductData(null);
                    } else {
                      // Clear list when switching to single mode
                      setScannedItems([]);
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Multiple Scan Mode
                </Typography>
              }
            />
          </Box>

          <Tabs 
            value={scanMethod} 
            onChange={(_, newValue) => {
              setScanMethod(newValue);
              setError(null);
              setScannedCode(null);
              if (newValue !== 'camera' && scanning) {
                stopScanning();
              }
            }}
            sx={{ mb: 3 }}
          >
            <Tab label="Camera" value="camera" icon={<PhotoCamera />} iconPosition="start" />
            <Tab label="Upload Image" value="file" icon={<PhotoCamera />} iconPosition="start" />
          </Tabs>

          {(() => {
            const isLocalhost = window.location.hostname === 'localhost' || 
                               window.location.hostname === '127.0.0.1' ||
                               window.location.hostname.startsWith('192.168.') ||
                               window.location.hostname.startsWith('10.') ||
                               window.location.hostname.startsWith('172.');
            
            // Show info message for HTTP on non-localhost (browsers may still allow it in some cases)
            if (!isLocalhost && !window.isSecureContext) {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Running on HTTP
                  </Typography>
                  <Typography variant="body2">
                    Camera access is being attempted on HTTP. Some browsers may require HTTPS for camera access on non-localhost connections.
                    If camera access fails, you may need to use HTTPS or access from localhost.
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Multiple Scan Mode - Info Message */}
          {multipleScanMode && scannedItems.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Multiple Scan Mode Active
              </Typography>
              <Typography variant="body2">
                Start scanning barcodes. Each scanned barcode will be automatically added to the list below.
                You can then select multiple items and update their material status all at once.
              </Typography>
            </Alert>
          )}

          {/* Multiple Scan Mode - Scanned Items List */}
          {multipleScanMode && scannedItems.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Scanned Items ({scannedItems.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={toggleSelectAll}
                      variant="outlined"
                    >
                      {scannedItems.every(item => item.selected) ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      size="small"
                      onClick={clearScannedItems}
                      variant="outlined"
                      color="error"
                    >
                      Clear All
                    </Button>
                  </Box>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 50 }}>
                          <Checkbox
                            checked={scannedItems.length > 0 && scannedItems.every(item => item.selected)}
                            indeterminate={scannedItems.some(item => item.selected) && !scannedItems.every(item => item.selected)}
                            onChange={toggleSelectAll}
                          />
                        </TableCell>
                        <TableCell><strong>Product</strong></TableCell>
                        <TableCell><strong>Room</strong></TableCell>
                        <TableCell><strong>Job #</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...scannedItems]
                        .sort((a, b) => {
                          const roomA = (a.product.room || 'N/A').toLowerCase();
                          const roomB = (b.product.room || 'N/A').toLowerCase();
                          return roomA.localeCompare(roomB);
                        })
                        .map((item) => {
                          // Find original index for proper selection/removal
                          const originalIndex = scannedItems.findIndex(
                            orig => orig.product_detail.barcode === item.product_detail.barcode &&
                                   orig.product_detail.id === item.product_detail.id
                          );
                          return (
                            <TableRow key={`${item.product_detail.barcode}-${originalIndex}`} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={item.selected}
                                  onChange={() => toggleItemSelection(originalIndex)}
                                />
                              </TableCell>
                              <TableCell>
                                {item.product.product_name} #{item.product_detail.number}
                              </TableCell>
                              <TableCell>{item.product.room || 'N/A'}</TableCell>
                              <TableCell>{item.product.job_number || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.product_detail.status || 'No Status'}
                                  size="small"
                                  color={item.product_detail.status ? 'primary' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  onClick={() => removeItem(originalIndex)}
                                  color="error"
                                >
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Bulk Status Update Controls */}
                {scannedItems.filter(item => item.selected).length > 0 && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Bulk Update Material Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {scannedItems.filter(item => item.selected).length} item(s) selected
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <Box sx={{ flex: 1, minWidth: '150px' }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="bulk-status-label">Material Status</InputLabel>
                          <Select
                            labelId="bulk-status-label"
                            value={bulkStatus}
                            label="Material Status"
                            onChange={(e) => setBulkStatus(e.target.value)}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {materialStatuses.map((status) => (
                              <MenuItem key={status.id} value={status.statusName}>
                                {status.statusName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleBulkStatusUpdate}
                        disabled={savingBulkStatus || !bulkStatus || loadingOptions}
                        sx={{ minWidth: '150px', height: '40px' }}
                      >
                        {savingBulkStatus ? 'Updating...' : `Update ${scannedItems.filter(item => item.selected).length} Item(s)`}
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Single Scan Mode - Single Item View */}
          {!multipleScanMode && scannedCode && (
            <Card sx={{ mb: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Barcode Scanned Successfully!
                </Typography>
                
                {loadingProduct && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Looking up product...</Typography>
                  </Box>
                )}
                {productData && (
                  <Box sx={{ mt: 2 }}>
                    {/* Section A: Read-only Label Info */}
                    <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', px: 2, py: 1.5, borderRadius: 1, mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {productData.product.job_address || ''}
                        {productData.product.apartment && ` Apt: ${productData.product.apartment}`}
                      </Typography>
                    </Box>

                    {/* Section B: Action/Report Controls */}
                    <Box sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)', p: 3, borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Action Controls
                      </Typography>
                      
                      {/* Function #1: Change Material Status */}
                      <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Function #1 — Change Material Status
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <Box sx={{ flex: 1, minWidth: '150px' }}>
                            <FormControl fullWidth size="small">
                              <InputLabel 
                                id="status-label" 
                                sx={{ 
                                  color: isDarkMode ? 'black' : 'white',
                                  '&.Mui-focused': {
                                    color: isDarkMode ? 'black' : 'white',
                                  }
                                }}
                              >
                                Material Status
                              </InputLabel>
                              <Select
                                labelId="status-label"
                                value={statusForm.status}
                                label="Material Status"
                                onChange={(e) => setStatusForm({ status: e.target.value })}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                  color: isDarkMode ? 'black' : 'white',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '& .MuiSelect-icon': {
                                    color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  }
                                }}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {materialStatuses.map((status) => (
                                  <MenuItem key={status.id} value={status.statusName}>
                                    {status.statusName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                          
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleStatusChange}
                            disabled={savingStatus || loadingOptions || statusForm.status === productData.product_detail.status}
                            sx={{ minWidth: '150px', height: '40px' }}
                          >
                            {savingStatus ? 'Updating...' : 'Update Status'}
                          </Button>
                        </Box>
                        
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                          Current: {productData.product_detail.status || 'No Status'}
                        </Typography>
                      </Box>

                      {/* Function #2: Add/Edit Glass Sizes */}
                      <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Function #2 — Add / Edit Glass Sizes (Dimensions)
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                          <Box sx={{ flex: 1, minWidth: '150px' }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Width (inches)"
                              type="number"
                              value={dimensionForm.width}
                              onChange={(e) => setDimensionForm({ ...dimensionForm, width: e.target.value })}
                              placeholder={productData.product_detail.width ? String(productData.product_detail.width) : 'Enter width'}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.1)',
                                '& .MuiInputBase-input': {
                                  color: isDarkMode ? 'black' : 'white',
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDarkMode ? 'black' : 'white',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: isDarkMode ? 'black' : 'white',
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                },
                              }}
                            />
                          </Box>
                          
                          <Box sx={{ flex: 1, minWidth: '150px' }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Height (inches)"
                              type="number"
                              value={dimensionForm.height}
                              onChange={(e) => setDimensionForm({ ...dimensionForm, height: e.target.value })}
                              placeholder={productData.product_detail.height ? String(productData.product_detail.height) : 'Enter height'}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.1)',
                                '& .MuiInputBase-input': {
                                  color: isDarkMode ? 'black' : 'white',
                                },
                                '& .MuiInputLabel-root': {
                                  color: isDarkMode ? 'black' : 'white',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: isDarkMode ? 'black' : 'white',
                                },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                },
                              }}
                            />
                          </Box>
                          
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleDimensionUpdate}
                            disabled={savingDimensions || loadingOptions || 
                              (dimensionForm.width === (productData.product_detail.width ? String(productData.product_detail.width) : '') &&
                               dimensionForm.height === (productData.product_detail.height ? String(productData.product_detail.height) : ''))}
                            sx={{ minWidth: '150px', height: '40px', alignSelf: 'flex-start' }}
                          >
                            {savingDimensions ? 'Updating...' : 'Update Sizes'}
                          </Button>
                        </Box>
                        
                        {(productData.product_detail.width || productData.product_detail.height) && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                            Current: {productData.product_detail.width && productData.product_detail.height
                              ? `${productData.product_detail.width}" × ${productData.product_detail.height}"`
                              : productData.product_detail.width
                              ? `Width: ${productData.product_detail.width}"`
                              : `Height: ${productData.product_detail.height}"`}
                          </Typography>
                        )}
                      </Box>

                      {/* Function #3: Set Type of Work */}
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                          Function #3 — Set Type of Work
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <Box sx={{ flex: 1, minWidth: '150px' }}>
                            <FormControl fullWidth size="small">
                              <InputLabel
                                id="type-of-work-label"
                                sx={{
                                  color: isDarkMode ? 'black' : 'white',
                                  '&.Mui-focused': {
                                    color: isDarkMode ? 'black' : 'white',
                                  }
                                }}
                              >
                                Type of Work
                              </InputLabel>
                              <Select
                                labelId="type-of-work-label"
                                multiple
                                value={typeOfWorkForm}
                                label="Type of Work"
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setTypeOfWorkForm(typeof value === 'string' ? value.split(',') : value);
                                }}
                                renderValue={(selected) => (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((val) => (
                                      <Chip
                                        key={val}
                                        label={val}
                                        size="small"
                                        onDelete={() => setTypeOfWorkForm(typeOfWorkForm.filter(v => v !== val))}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        sx={{
                                          color: isDarkMode ? 'black' : 'white',
                                          bgcolor: 'rgba(255,255,255,0.2)',
                                          '& .MuiChip-deleteIcon': {
                                            color: isDarkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                                            '&:hover': {
                                              color: isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)',
                                            },
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                )}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                  color: isDarkMode ? 'black' : 'white',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  },
                                  '& .MuiSelect-icon': {
                                    color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)',
                                  }
                                }}
                              >
                                {typeOfWorkOptions.map((option) => (
                                  <MenuItem key={option.id} value={option.workName}>
                                    {option.workName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>

                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleTypeOfWorkUpdate}
                            disabled={savingTypeOfWork || loadingOptions || typeOfWorkForm.join(', ') === (productData.product_detail.type_of_work || '')}
                            sx={{ minWidth: '150px', height: '40px' }}
                          >
                            {savingTypeOfWork ? 'Updating...' : 'Update Type'}
                          </Button>
                        </Box>

                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'rgba(255, 255, 255, 0.7)' }}>
                          Current: {productData.product_detail.type_of_work || 'None'}
                        </Typography>
                      </Box>

                      {/* Clear Button */}
                      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                        <Button
                          variant="outlined"
                          onClick={clearScannedCode}
                          sx={{ 
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            color: 'success.contrastText',
                            '&:hover': {
                              borderColor: 'rgba(255, 255, 255, 0.7)',
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            }
                          }}
                        >
                          Clear & Scan Next
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {scanMethod === 'camera' && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              {!scanning ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={startScanning}
                  disabled={scanning}
                  size="large"
                >
                  Start Scanning
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  onClick={stopScanning}
                  size="large"
                >
                  Stop Scanning
                </Button>
              )}
            </Box>
          )}

          {scanMethod === 'file' && (
            <Box sx={{ mb: 3 }}>
              <input
                ref={fileInputRef}
                accept="image/*"
                style={{ display: 'none' }}
                id="barcode-file-input"
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="barcode-file-input">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  size="large"
                  disabled={processingFile}
                  startIcon={<PhotoCamera />}
                >
                  {processingFile ? 'Processing...' : 'Upload Barcode Image'}
                </Button>
              </label>
              {processingFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Scanning image for barcode...
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Upload an image containing a barcode. Supported formats: JPG, PNG, GIF, WebP
              </Typography>
            </Box>
          )}

        </Box>

        {scanMethod === 'camera' && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 500,
              margin: '0 auto',
              bgcolor: 'grey.900',
              borderRadius: 2,
              overflow: 'hidden',
              minHeight: 300,
            }}
          >
            {/* Always render scanner element so it exists in DOM for Html5Qrcode initialization */}
            <Box
              id={scannerId}
              sx={{
                width: '100%',
                minHeight: 300,
                position: 'relative',
                '& video': {
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                },
                '& canvas': {
                  display: 'none',
                },
              }}
            />
            {!scanning && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: 'text.secondary',
                  p: 3,
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="body1">
                  Camera preview will appear here when scanning starts
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {scanning && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary" component="span">
              {multipleScanMode 
                ? `Scanning for barcodes... (${scannedItems.length} item(s) scanned)` 
                : 'Scanning for barcodes...'}
            </Typography>
            {multipleScanMode && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Scanner will continue running. Each scanned barcode will be added to the list above.
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
